/* eslint-disable @typescript-eslint/naming-convention */
import { useTranslation } from 'react-i18next';
import {
  AddFundsInstructions,
  AddFundsUsaInstructions,
} from '@efex-services/funds/types';

interface FundsInstructionsUSAProps {
  instructions?: AddFundsInstructions;
  newInstructions?: AddFundsUsaInstructions;
  loading?: boolean;
  currency?: string;
}

const FundsInstructionsUSA = ({
  instructions,
  newInstructions,
  loading,
  currency,
}: FundsInstructionsUSAProps) => {
  const { t } = useTranslation();
  const {
    account_number,
    account_type,
    bank_name,
    bank_address,
    beneficiary,
    beneficiary_address,
    clabe,
    routing_code,
    swift_code,
    transfer_method,
    nit,
  } = newInstructions || {};

  const validateFieldType = (field: string | string[]): string => {
    if (Array.isArray(field)) {
      return field
        .map((method) =>
          t([`aiMethod${method.toUpperCase()}`, method.toUpperCase()])
        )
        .join(', ');
    }
    try {
      if (Array.isArray(JSON.parse(field.replace(/'/g, '"'))))
        return JSON.parse(field.replace(/'/g, '"'))
          .map((method: string) =>
            t([`aiMethod${method.toUpperCase()}`, method.toUpperCase()])
          )
          .join(', ');
    } catch {
      console.error('Error parsing JSON');
    }
    return field;
  };

  if (loading) {
    return null;
  }

  if (newInstructions) {
    return (
      <div>
        {t('aiFundsToAddDisclaimer')}
        <div className="bg-[#F3F8FF] p-0 py-6 my-5 rounded-lg flex flex-col gap-1">
          {transfer_method ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuTransferMethod')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {validateFieldType(transfer_method)}
              </b>
            </div>
          ) : null}

          {account_number ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuAccount')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {account_number}
              </b>
            </div>
          ) : null}

          {clabe ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiClabeNumber')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {clabe}
              </b>
            </div>
          ) : null}

          {account_type ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuAccountType')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {validateFieldType(account_type)}
              </b>
            </div>
          ) : null}
          {routing_code && routing_code[0] ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuRoutingCodeACH')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {routing_code[0]}
              </b>
            </div>
          ) : null}
          {routing_code && routing_code[1] ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuRoutingCodeWire')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {routing_code[1]}
              </b>
            </div>
          ) : null}

          {beneficiary ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuReceipt')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {beneficiary}
              </b>
            </div>
          ) : null}
          {beneficiary_address ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6">
              <span className="inline-block w-full md:w-2/4 md:text-left" />
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {beneficiary_address}
              </b>
            </div>
          ) : null}
          {bank_name ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuBankName')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {bank_name}
              </b>
            </div>
          ) : null}
          {bank_address ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('aiFinturuBankAddress')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {bank_address}
              </b>
            </div>
          ) : null}
          {nit ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                NIT:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {nit}
              </b>
            </div>
          ) : null}
          {currency ? (
            <div className="flex flex-row flex-wrap justify-start w-full px-6 mt-5">
              <span className="inline-block w-full md:w-2/4 md:text-left">
                {t('afnCurrency')}:{' '}
              </span>
              <b className="inline-block w-full md:w-2/4 md:text-right">
                {currency}
              </b>
            </div>
          ) : null}
        </div>
        {swift_code ? (
          <>
            <span>{t('aiSwiftCodeMessage')}</span>
            <div className="blue_box_swift blue_box-content p-0">
              <div className="w-full">
                <span>{t('aiSwiftCode')}: </span>
                <b>{swift_code}</b>
              </div>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  if (instructions) {
    return (
      <div>
        {t('aiFundsToAddDisclaimer')}
        <div className="blue_box blue_box-content p-0">
          <div className="blue_box-info">
            {t('aiFinturuTransferMethod')}:
            <b>{validateFieldType(instructions.transferMethod || '')}</b>
          </div>

          {instructions.accountNumber ? (
            <div className="blue_box-info">
              <span>{t('aiFinturuAccount')}: </span>
              <b>{instructions.accountNumber}</b>
            </div>
          ) : null}
          {instructions.routingNumber ? (
            <div className="blue_box-info">
              <span>{t('aiFinturuRoutingNumber')}: </span>
              <b>{instructions.routingNumber}</b>
            </div>
          ) : null}
          {instructions.routingNumberAch && (
            <div className="blue_box-info">
              <span>{t('aiFinturuRoutingNumberAch')}: </span>
              <b>{instructions.routingNumberAch}</b>
            </div>
          )}
          {instructions.accountType ? (
            <div className="blue_box-info">
              <span>{t('aiFinturuAccountType')}: </span>
              <b>{validateFieldType(instructions.accountType || '')}</b>
            </div>
          ) : null}
          {instructions.routingCode && instructions.routingCode[0] ? (
            <div className="blue_box-info">
              <span> {t('aiFinturuRoutingCodeACH')}: </span>
              <b> {instructions.routingCode[0]}</b>
            </div>
          ) : null}
          {instructions.routingCode && instructions.routingCode[1] ? (
            <div className="blue_box-info">
              <span> {t('aiFinturuRoutingCodeWire')}: </span>
              <b> {instructions.routingCode[1]}</b>
            </div>
          ) : null}
          {instructions.beneficiary ? (
            <div className="blue_box-info">
              <span>{t('aiFinturuReceipt')}: </span>
              <b>{instructions.beneficiary}</b>
            </div>
          ) : null}
          {instructions.beneficiaryAddress ? (
            <div className="blue_box-info">
              <span />
              <b>{instructions.beneficiaryAddress}</b>
            </div>
          ) : null}
          {instructions.bankName ? (
            <div className="blue_box-info">
              <span>{t('aiFinturuBankName')}: </span>
              <b>{instructions.bankName}</b>
            </div>
          ) : null}
          {instructions.bankAddress ? (
            <div className="blue_box-info">
              <span />
              <b>{instructions.bankAddress}</b>
            </div>
          ) : null}
        </div>
        {instructions.swiftCode ? (
          <>
            <span>{t('aiSwiftCodeMessage')}</span>
            <div className="blue_box_swift blue_box-content p-0">
              <div className="blue_box-info">
                <span>{t('aiSwiftCode')}: </span>
                <b>{instructions.swiftCode}</b>
              </div>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return null;
};

export default FundsInstructionsUSA;
